/**
 * Photo-realistic SVG illustrations for the Energy Control starter-kit
 * compatibility step. Larger viewBox + gradients + soft shadows produce a
 * photo-like feel without depending on external stock imagery.
 *
 * Palette:
 *  - meter body: brushed white/grey (linear gradient + subtle highlight)
 *  - LCD / display: soft-blue backlight
 *  - P1 port: orange accent so it reads at thumbnail size
 *  - fuse cabinet: pale beige back panel + orange hazard header
 *  - roof / tiles: warm terracotta gradient
 *  - dashboard / phone: charcoal bezel with green graph lines
 *
 * Every illustration renders into a 200x160 viewBox; the parent card
 * stretches this to the full width of a mobile card (aspect 5/4).
 */

// ---------- Gradient + filter defs (shared once per SVG) ----------
function defs(extra) {
  return (
    <defs>
      {/* Brushed metal for meter casings */}
      <linearGradient id="metalBody" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F5F5F4" />
        <stop offset="45%" stopColor="#E5E5E4" />
        <stop offset="100%" stopColor="#C8CAC7" />
      </linearGradient>
      <linearGradient id="metalEdge" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#B3B6B3" />
        <stop offset="100%" stopColor="#8A8D8A" />
      </linearGradient>
      {/* LCD display backlight */}
      <linearGradient id="lcd" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#CFE8E2" />
        <stop offset="100%" stopColor="#9EC8BF" />
      </linearGradient>
      {/* Fuse cabinet panel */}
      <linearGradient id="panel" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FAFAF7" />
        <stop offset="100%" stopColor="#E0DED5" />
      </linearGradient>
      <linearGradient id="hazard" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#D97706" />
      </linearGradient>
      {/* Roof / tiles */}
      <linearGradient id="roof" x1="0" y1="0" x2="1" y2="0.6">
        <stop offset="0%" stopColor="#B45309" />
        <stop offset="100%" stopColor="#D97706" />
      </linearGradient>
      {/* Solar panel glass */}
      <linearGradient id="pvGlass" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1E3A5F" />
        <stop offset="100%" stopColor="#0F1F33" />
      </linearGradient>
      {/* Phone / dashboard screen */}
      <linearGradient id="screen" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0F3D38" />
        <stop offset="100%" stopColor="#0A2A26" />
      </linearGradient>
      {/* Analog meter dial */}
      <radialGradient id="dial" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#E7E5E0" />
      </radialGradient>
      <linearGradient id="bakelite" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5B3A2E" />
        <stop offset="100%" stopColor="#3B2319" />
      </linearGradient>
      {/* Soft shadow */}
      <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="1.8" />
      </filter>
      {extra}
    </defs>
  );
}

function frame(children, label, extraDefs) {
  return (
    <svg
      viewBox="0 0 200 160"
      className="w-full h-full"
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid slice"
    >
      {/* Warm neutral wall background so the subject pops like a photo */}
      <rect x="0" y="0" width="200" height="160" fill="#EEE8DC" />
      <rect x="0" y="118" width="200" height="42" fill="#D9CFB8" />
      {defs(extraDefs)}
      {children}
    </svg>
  );
}

// ========== METER-related ==========

// 1. Slimme meter met P1-poort (modern meter, P1 port clearly marked)
const meter_p1 = () =>
  frame(
    <>
      {/* Ground shadow */}
      <ellipse cx="100" cy="142" rx="56" ry="5" fill="#000" opacity="0.12" filter="url(#softShadow)" />
      {/* Meter outer casing */}
      <rect x="42" y="28" width="116" height="104" rx="9" fill="url(#metalEdge)" />
      <rect x="46" y="32" width="108" height="96" rx="7" fill="url(#metalBody)" />
      {/* Top brand bar */}
      <rect x="54" y="38" width="92" height="10" rx="2" fill="#0F3D38" />
      <circle cx="62" cy="43" r="2.3" fill="#A7F3D0" />
      <rect x="70" y="41" width="38" height="4" rx="1" fill="#E6EFEC" opacity="0.7" />
      {/* LCD display */}
      <rect x="54" y="54" width="92" height="28" rx="2" fill="#111" />
      <rect x="56" y="56" width="88" height="24" rx="1.5" fill="url(#lcd)" />
      <text x="100" y="73" textAnchor="middle" fontSize="14" fontWeight="800" fontFamily="monospace" fill="#0F3D38">
        0824.7
      </text>
      <text x="138" y="78" textAnchor="middle" fontSize="5" fontWeight="700" fill="#0F3D38">
        kWh
      </text>
      {/* Indicator LEDs */}
      <circle cx="62" cy="90" r="2.6" fill="#10B981" />
      <circle cx="72" cy="90" r="2.6" fill="#D1D5DB" />
      <circle cx="82" cy="90" r="2.6" fill="#D1D5DB" />
      {/* Buttons */}
      <rect x="95" y="87" width="10" height="6" rx="1" fill="#9CA3AF" />
      <rect x="108" y="87" width="10" height="6" rx="1" fill="#9CA3AF" />
      {/* P1 port: clearly marked with arrow callout */}
      <rect x="58" y="104" width="30" height="18" rx="2" fill="#374151" />
      <rect x="60" y="106" width="26" height="14" rx="1.5" fill="url(#hazard)" stroke="#92400E" strokeWidth="0.6" />
      <text x="73" y="117" textAnchor="middle" fontSize="8" fontWeight="900" fill="white">
        P1
      </text>
      {/* Callout arrow + label */}
      <path d="M 90 113 Q 116 110 130 108" stroke="#B91C1C" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <polygon points="88,110 88,116 93,113" fill="#B91C1C" />
      <rect x="128" y="96" width="28" height="22" rx="3" fill="#FEF3C7" stroke="#B45309" strokeWidth="0.8" />
      <text x="142" y="106" textAnchor="middle" fontSize="6" fontWeight="800" fill="#92400E">
        P1
      </text>
      <text x="142" y="113" textAnchor="middle" fontSize="4.5" fontWeight="600" fill="#92400E">
        poort
      </text>
      {/* Serial number strip */}
      <rect x="110" y="124" width="34" height="3" rx="0.5" fill="#9CA3AF" opacity="0.7" />
    </>,
    'Slimme meter met P1-poort',
  );

// 2. Slimme meter, P1 onbekend (modern meter, no visible P1 callout + question mark)
const meter_p1_unclear = () =>
  frame(
    <>
      <ellipse cx="100" cy="142" rx="56" ry="5" fill="#000" opacity="0.12" filter="url(#softShadow)" />
      <rect x="42" y="28" width="116" height="104" rx="9" fill="url(#metalEdge)" />
      <rect x="46" y="32" width="108" height="96" rx="7" fill="url(#metalBody)" />
      <rect x="54" y="38" width="92" height="10" rx="2" fill="#0F3D38" />
      <circle cx="62" cy="43" r="2.3" fill="#A7F3D0" />
      <rect x="70" y="41" width="38" height="4" rx="1" fill="#E6EFEC" opacity="0.7" />
      <rect x="54" y="54" width="92" height="28" rx="2" fill="#111" />
      <rect x="56" y="56" width="88" height="24" rx="1.5" fill="url(#lcd)" />
      <text x="100" y="73" textAnchor="middle" fontSize="14" fontWeight="800" fontFamily="monospace" fill="#0F3D38">
        1204.3
      </text>
      <circle cx="62" cy="90" r="2.6" fill="#10B981" />
      <circle cx="72" cy="90" r="2.6" fill="#D1D5DB" />
      <circle cx="82" cy="90" r="2.6" fill="#D1D5DB" />
      <rect x="95" y="87" width="10" height="6" rx="1" fill="#9CA3AF" />
      <rect x="108" y="87" width="10" height="6" rx="1" fill="#9CA3AF" />
      {/* Vague cable opening — NO clear P1 marking */}
      <rect x="64" y="104" width="72" height="18" rx="3" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.8" strokeDasharray="2 2" />
      <text x="100" y="116" textAnchor="middle" fontSize="6" fontWeight="600" fill="#9CA3AF">
        ? onbekend
      </text>
      {/* Large question mark overlay */}
      <circle cx="170" cy="50" r="16" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="1.8" />
      <text x="170" y="57" textAnchor="middle" fontSize="20" fontWeight="900" fill="#B45309">
        ?
      </text>
    </>,
    'Slimme meter, P1 onbekend',
  );

// 3. Oude / analoge meter (bakelite body, round dial with pointer)
const meter_old_analog = () =>
  frame(
    <>
      <ellipse cx="100" cy="142" rx="54" ry="5" fill="#000" opacity="0.14" filter="url(#softShadow)" />
      {/* Wooden back plate */}
      <rect x="34" y="22" width="132" height="110" rx="3" fill="#9A7449" />
      <rect x="36" y="24" width="128" height="106" rx="2" fill="#B08556" />
      {/* Bakelite housing */}
      <rect x="48" y="30" width="104" height="96" rx="6" fill="url(#bakelite)" />
      <rect x="52" y="34" width="96" height="88" rx="4" fill="#4A2E22" />
      {/* Big round dial */}
      <circle cx="100" cy="72" r="28" fill="url(#dial)" stroke="#1F2937" strokeWidth="1.5" />
      <circle cx="100" cy="72" r="24" fill="none" stroke="#1F2937" strokeWidth="0.6" />
      {/* Tick marks around dial */}
      {[...Array(12)].map((_, i) => {
        const angle = (i * 30 * Math.PI) / 180;
        const x1 = 100 + Math.sin(angle) * 24;
        const y1 = 72 - Math.cos(angle) * 24;
        const x2 = 100 + Math.sin(angle) * 27;
        const y2 = 72 - Math.cos(angle) * 27;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1F2937" strokeWidth="1" />;
      })}
      {/* Dial pointer */}
      <line x1="100" y1="72" x2="114" y2="56" stroke="#B91C1C" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="100" cy="72" r="3" fill="#1F2937" />
      {/* Digit counter strip */}
      <rect x="72" y="104" width="56" height="12" rx="1" fill="#1A1A1A" />
      <rect x="74" y="106" width="52" height="8" rx="0.5" fill="#F5F5F4" />
      <text x="100" y="113" textAnchor="middle" fontSize="7" fontWeight="800" fontFamily="monospace" fill="#1F2937">
        047291
      </text>
      {/* Two terminals at the bottom corners */}
      <circle cx="60" cy="120" r="3.5" fill="#FCD34D" stroke="#92400E" strokeWidth="0.8" />
      <circle cx="140" cy="120" r="3.5" fill="#FCD34D" stroke="#92400E" strokeWidth="0.8" />
      {/* Small "draaischijf" tag */}
      <rect x="84" y="38" width="32" height="6" rx="1" fill="#FDE68A" />
      <text x="100" y="43" textAnchor="middle" fontSize="4.5" fontWeight="700" fill="#92400E">
        ANALOOG
      </text>
    </>,
    'Oude / analoge meter',
  );

// ========== CABINET-related ==========

// 4. 1-fase aansluiting (narrow cabinet, single column of breakers)
const cabinet_1fase = () =>
  frame(
    <>
      <ellipse cx="100" cy="145" rx="52" ry="5" fill="#000" opacity="0.14" filter="url(#softShadow)" />
      {/* Narrow cabinet */}
      <rect x="68" y="18" width="64" height="120" rx="3" fill="#9CA3AF" />
      <rect x="70" y="20" width="60" height="116" rx="2" fill="url(#panel)" />
      {/* Orange hazard strip top */}
      <rect x="72" y="22" width="56" height="12" rx="1" fill="url(#hazard)" />
      <text x="100" y="30" textAnchor="middle" fontSize="5" fontWeight="800" fill="white">
        HOOFD • 1-FASE
      </text>
      {/* Main switch — single lever */}
      <rect x="86" y="38" width="28" height="14" rx="1" fill="#1F2937" />
      <rect x="96" y="40" width="8" height="10" rx="0.5" fill="#DC2626" />
      <circle cx="100" cy="45" r="1.6" fill="#FCA5A5" />
      {/* DIN rail header */}
      <rect x="72" y="54" width="56" height="3" fill="#9CA3AF" />
      {/* Breakers in a SINGLE column (hallmark of 1-fase) */}
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i}>
          <rect x={78} y={62 + i * 12} width="44" height="9" rx="1" fill="#F3F4F6" stroke="#6B7280" strokeWidth="0.5" />
          <rect x={92} y={64 + i * 12} width="4" height="5" rx="0.5" fill="#1F2937" />
          <text x={112} y={69 + i * 12} textAnchor="end" fontSize="4" fontWeight="600" fill="#6B7280">
            16A
          </text>
        </g>
      ))}
      {/* Large "1×" badge */}
      <circle cx="48" cy="72" r="16" fill="#0F3D38" />
      <text x="48" y="79" textAnchor="middle" fontSize="18" fontWeight="900" fill="white">
        1×
      </text>
      <text x="48" y="94" textAnchor="middle" fontSize="5" fontWeight="700" fill="#0F3D38">
        FASE
      </text>
    </>,
    '1-fase aansluiting',
  );

// 5. 3-fase aansluiting (wider cabinet, 3 columns of breakers + triple lever)
const cabinet_3fase = () =>
  frame(
    <>
      <ellipse cx="100" cy="145" rx="66" ry="5" fill="#000" opacity="0.14" filter="url(#softShadow)" />
      {/* Wider cabinet */}
      <rect x="32" y="18" width="136" height="120" rx="3" fill="#9CA3AF" />
      <rect x="34" y="20" width="132" height="116" rx="2" fill="url(#panel)" />
      <rect x="36" y="22" width="128" height="12" rx="1" fill="url(#hazard)" />
      <text x="100" y="30" textAnchor="middle" fontSize="5" fontWeight="800" fill="white">
        HOOFD • 3-FASE
      </text>
      {/* Main switch — 3-ganged lever */}
      <rect x="76" y="38" width="48" height="14" rx="1" fill="#1F2937" />
      {[0, 1, 2].map((i) => (
        <rect key={i} x={82 + i * 13} y={40} width="8" height="10" rx="0.5" fill="#DC2626" />
      ))}
      {/* DIN rails — three rows */}
      {[0, 1, 2].map((row) => (
        <g key={`row-${row}`}>
          <rect x="36" y={56 + row * 26} width="128" height="3" fill="#9CA3AF" />
          {[0, 1, 2].map((col) => (
            <g key={`b-${row}-${col}`}>
              <rect
                x={40 + col * 42}
                y={62 + row * 26}
                width="38"
                height="14"
                rx="1"
                fill="#F3F4F6"
                stroke="#6B7280"
                strokeWidth="0.5"
              />
              <rect x={52 + col * 42} y={65 + row * 26} width="4" height="8" rx="0.5" fill="#1F2937" />
              <text x={70 + col * 42} y={72 + row * 26} textAnchor="end" fontSize="4" fontWeight="600" fill="#6B7280">
                {col === 1 ? '25A' : '16A'}
              </text>
            </g>
          ))}
        </g>
      ))}
      {/* Large "3×" badge */}
      <circle cx="20" cy="72" r="14" fill="#0F3D38" />
      <text x="20" y="78" textAnchor="middle" fontSize="16" fontWeight="900" fill="white">
        3×
      </text>
    </>,
    '3-fase aansluiting',
  );

// ========== LIVE-INSIGHT related ==========

// 6. Ik heb al live inzicht (phone with dashboard + P1-dongle)
const live_dashboard = () =>
  frame(
    <>
      <ellipse cx="100" cy="146" rx="60" ry="5" fill="#000" opacity="0.14" filter="url(#softShadow)" />
      {/* Meter stub on the left with P1 dongle plugged in */}
      <rect x="14" y="58" width="48" height="54" rx="4" fill="url(#metalEdge)" />
      <rect x="16" y="60" width="44" height="50" rx="3" fill="url(#metalBody)" />
      <rect x="20" y="64" width="36" height="14" rx="1" fill="#111" />
      <rect x="22" y="66" width="32" height="10" rx="0.5" fill="url(#lcd)" />
      <text x="38" y="74" textAnchor="middle" fontSize="5" fontWeight="800" fontFamily="monospace" fill="#0F3D38">
        0824
      </text>
      {/* P1 dongle */}
      <rect x="28" y="86" width="20" height="14" rx="1.5" fill="url(#hazard)" stroke="#92400E" strokeWidth="0.6" />
      <text x="38" y="95" textAnchor="middle" fontSize="5" fontWeight="900" fill="white">
        P1
      </text>
      {/* Wifi waves between dongle and phone */}
      <path d="M 52 90 Q 80 70 110 80" stroke="#10B981" strokeWidth="1.4" fill="none" strokeDasharray="2 2" />
      <path d="M 54 96 Q 80 80 108 88" stroke="#10B981" strokeWidth="1.4" fill="none" strokeDasharray="2 2" />
      {/* Phone */}
      <rect x="108" y="24" width="68" height="114" rx="8" fill="#1F2937" />
      <rect x="112" y="28" width="60" height="106" rx="5" fill="url(#screen)" />
      {/* Status bar */}
      <rect x="118" y="32" width="6" height="2" rx="0.5" fill="#A7F3D0" opacity="0.7" />
      <rect x="128" y="32" width="14" height="2" rx="0.5" fill="#A7F3D0" opacity="0.4" />
      {/* Header "Live kW" */}
      <text x="142" y="46" textAnchor="middle" fontSize="5" fontWeight="700" fill="#A7F3D0">
        NU
      </text>
      <text x="142" y="62" textAnchor="middle" fontSize="13" fontWeight="900" fill="#ECFDF5">
        1.84
      </text>
      <text x="142" y="70" textAnchor="middle" fontSize="4.5" fontWeight="700" fill="#A7F3D0">
        kW
      </text>
      {/* Mini area-graph */}
      <path
        d="M 116 118 L 122 110 L 128 112 L 134 102 L 140 108 L 146 96 L 152 104 L 158 92 L 164 100 L 168 118 Z"
        fill="#10B981"
        opacity="0.75"
      />
      <path
        d="M 116 118 L 122 110 L 128 112 L 134 102 L 140 108 L 146 96 L 152 104 L 158 92 L 164 100"
        stroke="#A7F3D0"
        strokeWidth="1.2"
        fill="none"
      />
      {/* Legend dots */}
      <circle cx="122" cy="128" r="1.6" fill="#10B981" />
      <text x="127" y="130" fontSize="4" fontWeight="600" fill="#A7F3D0">
        verbruik
      </text>
      {/* Home-button hint */}
      <rect x="136" y="136" width="12" height="1.2" rx="0.6" fill="#6B7280" />
    </>,
    'Ik heb al live inzicht',
  );

// 7. Nog geen live inzicht (plain meter, no dongle, no phone)
const no_live_dashboard = () =>
  frame(
    <>
      <ellipse cx="100" cy="145" rx="52" ry="5" fill="#000" opacity="0.14" filter="url(#softShadow)" />
      {/* Meter only — empty cable slot, no dongle */}
      <rect x="56" y="30" width="88" height="96" rx="6" fill="url(#metalEdge)" />
      <rect x="60" y="34" width="80" height="88" rx="4" fill="url(#metalBody)" />
      <rect x="68" y="40" width="64" height="10" rx="2" fill="#0F3D38" />
      <rect x="68" y="56" width="64" height="22" rx="2" fill="#111" />
      <rect x="70" y="58" width="60" height="18" rx="1" fill="url(#lcd)" />
      <text x="100" y="72" textAnchor="middle" fontSize="11" fontWeight="800" fontFamily="monospace" fill="#0F3D38">
        0824.7
      </text>
      <circle cx="74" cy="86" r="2" fill="#10B981" />
      <circle cx="82" cy="86" r="2" fill="#D1D5DB" />
      <circle cx="90" cy="86" r="2" fill="#D1D5DB" />
      {/* Empty P1 port (no cable/dongle attached) */}
      <rect x="82" y="98" width="22" height="14" rx="2" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.8" />
      <rect x="84" y="100" width="18" height="10" rx="1" fill="#F9FAFB" />
      <text x="93" y="108" textAnchor="middle" fontSize="5" fontWeight="700" fill="#9CA3AF">
        leeg
      </text>
      {/* Greyed out phone silhouette with slash */}
      <g opacity="0.5">
        <rect x="154" y="54" width="28" height="52" rx="3" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.8" />
        <rect x="157" y="57" width="22" height="46" rx="1.5" fill="#F3F4F6" />
        <line x1="150" y1="108" x2="186" y2="50" stroke="#DC2626" strokeWidth="2.4" strokeLinecap="round" />
      </g>
      <text x="168" y="120" textAnchor="middle" fontSize="4.5" fontWeight="700" fill="#9CA3AF">
        geen dashboard
      </text>
    </>,
    'Nog geen live inzicht',
  );

// ========== UNKNOWN / fallback ==========

// 8. Weet ik niet — friendly shrug circle with big "?"
const unknown = () =>
  frame(
    <>
      <ellipse cx="100" cy="142" rx="44" ry="4" fill="#000" opacity="0.1" filter="url(#softShadow)" />
      <circle cx="100" cy="78" r="48" fill="#F5F3EE" stroke="#D6D3CB" strokeWidth="2" />
      <circle cx="100" cy="78" r="42" fill="#FEF9E7" stroke="#F59E0B" strokeWidth="1.2" strokeDasharray="3 3" />
      <text x="100" y="96" textAnchor="middle" fontSize="56" fontWeight="900" fill="#B45309">
        ?
      </text>
      <rect x="62" y="128" width="76" height="10" rx="2" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="0.8" />
      <text x="100" y="135" textAnchor="middle" fontSize="5.5" fontWeight="800" fill="#92400E">
        GEEN PROBLEEM
      </text>
    </>,
    'Weet ik niet',
  );

// Not-applicable (kept for legacy compatibility with older data)
const not_applicable = () =>
  frame(
    <>
      <ellipse cx="100" cy="142" rx="44" ry="4" fill="#000" opacity="0.1" filter="url(#softShadow)" />
      <circle cx="100" cy="78" r="48" fill="#F5F3EE" stroke="#D6D3CB" strokeWidth="2" />
      <line x1="68" y1="78" x2="132" y2="78" stroke="#6B7280" strokeWidth="5" strokeLinecap="round" />
      <text x="100" y="130" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#6B7280">
        NIET VAN TOEPASSING
      </text>
    </>,
    'Niet van toepassing',
  );

// Legacy solar illustrations (kept so old admin records render correctly)
const solar_with_inverter = () =>
  frame(
    <>
      <rect x="14" y="58" width="172" height="60" fill="url(#roof)" />
      <rect x="28" y="30" width="110" height="34" rx="2" fill="url(#pvGlass)" stroke="#0F1F33" strokeWidth="1" />
      {[...Array(4)].map((_, i) => (
        <line key={i} x1={28 + i * 27.5} y1="30" x2={28 + i * 27.5} y2="64" stroke="#2E4D73" strokeWidth="0.8" />
      ))}
      <line x1="28" y1="47" x2="138" y2="47" stroke="#2E4D73" strokeWidth="0.8" />
      <rect x="152" y="78" width="34" height="30" rx="2" fill="#F5F5F4" stroke="#6B7280" strokeWidth="1" />
      <text x="169" y="96" textAnchor="middle" fontSize="6" fontWeight="800" fill="#0F3D38">
        DC/AC
      </text>
    </>,
    'Zonnepanelen met omvormer',
  );

const solar_unknown = () =>
  frame(
    <>
      <rect x="14" y="58" width="172" height="60" fill="url(#roof)" />
      <rect x="28" y="30" width="110" height="34" rx="2" fill="url(#pvGlass)" stroke="#0F1F33" strokeWidth="1" />
      <circle cx="164" cy="70" r="14" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="1.5" />
      <text x="164" y="78" textAnchor="middle" fontSize="18" fontWeight="900" fill="#B45309">
        ?
      </text>
    </>,
    'Zonnepanelen aanwezig, type onbekend',
  );

export const Illustrations = {
  // NEW + photo-realistic meter set
  meter_p1,
  meter_p1_unclear,
  meter_old_analog,
  // Legacy alias: old starter-kit leads used 'old_unclear' — map to the new analog visual
  meter_old: meter_old_analog,
  // Cabinet
  cabinet_1fase,
  cabinet_3fase,
  // Live insight (NEW question)
  live_dashboard,
  no_live_dashboard,
  // Fallbacks
  unknown,
  not_applicable,
  // Legacy solar (kept for admin backward compat only)
  solar_with_inverter,
  solar_unknown,
};

export default Illustrations;
